import { useState, useEffect } from "react";
import { Coins, X, Heart } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const PRESETS = [1, 5, 10, 25];

export default function TipModal({ open, onClose, mode, recipient }) {
  // mode: "community" | "individual"
  const { user } = useAuth();
  const [amount, setAmount] = useState(5);
  const [custom, setCustom] = useState("");
  const [message, setMessage] = useState("");
  const [anon, setAnon] = useState(false);
  const [anonFull, setAnonFull] = useState(false);
  const [loading, setLoading] = useState(false);
  const [quote, setQuote] = useState(null);
  const [recurring, setRecurring] = useState(false);
  const [methods, setMethods] = useState({ stripe: true, paypal: false });

  const isCommunity = mode === "community";
  const finalAmount = custom ? parseFloat(custom) : amount;

  useEffect(() => {
    if (open) {
      api.get("/payments/methods").then((r) => setMethods(r.data)).catch(() => {});
      // supporters give anonymously by default
      setAnon(user?.account_type === "supporter");
      setAnonFull(false);
    }
  }, [open, user]);

  useEffect(() => {
    if (!finalAmount || finalAmount < 1) { setQuote(null); return; }
    const t = setTimeout(() => {
      api.get(`/fees/quote?amount=${finalAmount}`).then((r) => setQuote(r.data)).catch(() => setQuote(null));
    }, 200);
    return () => clearTimeout(t);
  }, [finalAmount]);

  const submit = async () => {
    if (!finalAmount || finalAmount < 1) { toast.error("Minimum contribution is $1."); return; }
    setLoading(true);
    try {
      if (recurring && !isCommunity) {
        const { data } = await api.post("/pledges/checkout", {
          to_user_id: recipient?.user_id, amount: finalAmount, origin_url: window.location.origin,
        });
        window.location.href = data.checkout_url;
        return;
      }
      const { data } = await api.post("/tips/checkout", {
        tip_type: isCommunity ? "community" : "individual",
        to_user_id: recipient?.user_id || null,
        amount: finalAmount,
        message,
        anonymous: anon || anonFull,
        anonymous_full: anonFull,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiErr(e));
      setLoading(false);
    }
  };

  const payWithPayPal = async () => {
    if (!finalAmount || finalAmount < 1) { toast.error("Minimum is $1."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/paypal/order", {
        tip_type: isCommunity ? "community" : "individual",
        to_user_id: recipient?.user_id || null, amount: finalAmount, origin_url: window.location.origin,
      });
      window.location.href = data.approve_url;
    } catch (e) { toast.error(apiErr(e)); setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={isCommunity ? "community-tip-modal" : "individual-tipping-modal"} className="sm:max-w-md rounded-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2 text-bronze">
            <Coins className="w-6 h-6 text-penny" />
            {isCommunity ? "Drop in the Community Jar" : `Leave a penny for ${recipient?.name || "them"}`}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground -mt-1">
          {isCommunity
            ? "Your gift is split equally among every member seeking support — a little kindness spread wide."
            : "100% of your tip goes straight to their wallet."}
        </p>

        <div className="grid grid-cols-4 gap-2 mt-2">
          {PRESETS.map((p) => (
            <button key={p} data-testid={`preset-${p}`}
              onClick={() => { setAmount(p); setCustom(""); }}
              className={`py-3 rounded-2xl font-mono font-bold text-lg transition-all ${
                !custom && amount === p ? "bg-copper text-white scale-105 shadow-md" : "bg-secondary text-foreground hover:bg-accent"
              }`}>
              ${p}
            </button>
          ))}
        </div>

        <div>
          <Label className="text-xs uppercase tracking-widest text-copper font-mono">Custom amount</Label>
          <div className="relative mt-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
            <Input data-testid="custom-amount-input" type="number" min="1" step="1" value={custom}
              onChange={(e) => setCustom(e.target.value)} placeholder="Other" className="pl-7 rounded-xl" />
          </div>
        </div>

        <Textarea data-testid="tip-message-input" value={message} onChange={(e) => setMessage(e.target.value)}
          placeholder="Add a kind note (optional)" className="rounded-xl resize-none" rows={2} />

        <div className="flex items-center justify-between">
          <Label htmlFor="anon" className="text-sm">Hide my name publicly</Label>
          <Switch id="anon" data-testid="anon-toggle" checked={anon || anonFull} disabled={anonFull} onCheckedChange={setAnon} />
        </div>
        {!isCommunity && (
          <div className="flex items-center justify-between -mt-1">
            <Label htmlFor="anonfull" className="text-xs text-muted-foreground">Fully anonymous <span className="text-[11px]">(hidden even from them)</span></Label>
            <Switch id="anonfull" data-testid="anon-full-toggle" checked={anonFull} onCheckedChange={(v) => { setAnonFull(v); if (v) setAnon(true); }} />
          </div>
        )}

        {!isCommunity && (
          <div className="flex items-center justify-between bg-secondary rounded-2xl px-3 py-2">
            <Label htmlFor="recurring" className="text-sm">Make it monthly 💞 <span className="text-xs text-muted-foreground">(ongoing support)</span></Label>
            <Switch id="recurring" data-testid="recurring-toggle" checked={recurring} onCheckedChange={setRecurring} />
          </div>
        )}

        <Button data-testid="tip-submit-btn" onClick={submit} disabled={loading}
          className="w-full bg-copper hover:bg-copper/90 text-white rounded-full py-6 text-base">
          <Heart className="w-4 h-4 mr-2" />
          {loading ? "Opening secure checkout…" : recurring && !isCommunity ? `Pledge $${(quote?.total ?? finalAmount ?? 0).toFixed(2)}/mo` : `Give $${(quote?.total ?? finalAmount ?? 0).toFixed(2)}`}
        </Button>
        {methods.paypal && !recurring && (
          <Button data-testid="paypal-btn" onClick={payWithPayPal} disabled={loading} variant="outline"
            className="w-full rounded-full py-6 text-base border-[#003087] text-[#003087] hover:bg-[#003087]/10">
            Pay with PayPal
          </Button>
        )}
        {quote && (
          <div data-testid="fee-breakdown" className="text-sm bg-secondary rounded-2xl p-3 space-y-1">
            <div className="flex justify-between"><span className="text-muted-foreground">{isCommunity ? "To the jar" : "To them"}</span><span className="font-semibold">${quote.donation.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Platform fee (5%)</span><span>${quote.platform_fee.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Payment processing</span><span>${quote.stripe_fee.toFixed(2)}</span></div>
            <div className="flex justify-between border-t border-border pt-1 mt-1 font-semibold text-bronze"><span>You pay</span><span>${quote.total.toFixed(2)}</span></div>
            <p className="text-[11px] text-sage font-medium pt-1">💛 {isCommunity ? "Everyone" : "They"} receive{isCommunity ? "" : "s"} the full ${quote.donation.toFixed(2)} — 100%, no cut taken from those in need.</p>
          </div>
        )}
        <p className="text-[11px] text-center text-muted-foreground">Secure payment via Stripe · Test card 4242 4242 4242 4242</p>
      </DialogContent>
    </Dialog>
  );
}
